/**
 * Interface for a single mouse movement data point.
 */
interface MousePoint {
  x: number
  y: number
  time: number // performance.now() timestamp
}

/**
 * Interface for the detection result.
 */
export interface DetectionResult {
  isBot: boolean | null // null initially, or if not enough data
  reasons: string[]
}

/**
 * Interface for the constructor options of MouseBotDetector.
 */
interface MouseBotDetectorOptions {
  minMovementPoints: number
  maxStraightnessDeviation: number
  minMovementSpeedPxPerMs: number
  maxMovementSpeedPxPerMs: number
  minJitterPercentage: number
  maxMovementPoints: number // Maximum number of movement points to store
}

/**
 * A simple client-side heuristic for detecting bot-like mouse movements.
 * Collects mouse movement data and analyzes patterns to differentiate
 * between human and potentially bot-like behavior.
 */
class MouseBotDetector {
  private options: MouseBotDetectorOptions
  private mouseMovements: MousePoint[] = []
  private detectionResult: DetectionResult
  private isMonitoring: boolean = false
  private idleTimeout: number | null = null
  private listeners: ((result: DetectionResult) => void)[] = []

  constructor(options: Partial<MouseBotDetectorOptions> = {}) {
    // Default options for the heuristic
    this.options = {
      minMovementPoints: 100,
      maxStraightnessDeviation: 0.05, // Smaller value = straighter line
      minMovementSpeedPxPerMs: 0.1,
      maxMovementSpeedPxPerMs: 10,
      minJitterPercentage: 0.005,
      maxMovementPoints: 300
    }

    // Override default options with provided ones
    Object.assign(this.options, options)

    this.detectionResult = {
      isBot: null, // Undetermined initially
      reasons: []
    }

    // Bind event handler to the class instance
    this.handleMouseMove = this.handleMouseMove.bind(this)
  }

  /**
   * Adds a listener function to be called when the detection result changes.
   * @param {function(DetectionResult)} listener
   */
  public addResultListener(listener: (result: DetectionResult) => void): void {
    if (typeof listener === 'function') {
      this.listeners.push(listener)
    }
  }

  /**
   * Removes a listener function.
   * @param {function(DetectionResult)} listener
   */
  public removeResultListener(
    listener: (result: DetectionResult) => void
  ): void {
    this.listeners = this.listeners.filter((l) => l !== listener)
  }

  /**
   * Notifies all registered listeners about the current detection result.
   * @private
   */
  private _notifyListeners(): void {
    if (!this.isMonitoring) {
      return
    }
    this.listeners.forEach((listener) => listener(this.detectionResult))
  }

  /**
   * Starts monitoring mouse movements.
   */
  public startMonitoring(): void {
    if (this.isMonitoring) {
      return
    }
    this.isMonitoring = true
    this.mouseMovements = []
    this.detectionResult = {
      isBot: null,
      reasons: []
    }

    document.addEventListener('mousemove', this.handleMouseMove)
  }

  /**
   * Stopps monitoring mouse movements.
   * Should be called when a decision has been made or the page is unloaded.
   */
  public stopMonitoring(): void {
    if (!this.isMonitoring) {
      return
    }
    this.isMonitoring = false
    document.removeEventListener('mousemove', this.handleMouseMove)
    if (this.idleTimeout !== null) {
      clearTimeout(this.idleTimeout)
      this.idleTimeout = null
    }

    this._notifyListeners() // Notify when monitoring stops
  }

  /**
   * Event handler for mouse movements.
   * @param {MouseEvent} event
   */
  private handleMouseMove(event: MouseEvent): void {
    if (!this.isMonitoring) return

    const now = performance.now()
    const lastMovement =
      this.mouseMovements.length > 0
        ? this.mouseMovements[this.mouseMovements.length - 1]
        : null

    const newPoint: MousePoint = {
      x: event.clientX,
      y: event.clientY,
      time: now
    }

    // Add point only if mouse has actually moved
    if (
      !lastMovement ||
      lastMovement.x !== newPoint.x ||
      lastMovement.y !== newPoint.y
    ) {
      this.mouseMovements.push(newPoint)
    }

    // Perform analysis only if enough data points have been collected
    if (this.mouseMovements.length >= this.options.minMovementPoints) {
      const oldIsBot = this.detectionResult.isBot // Snapshot current result's isBot status
      const oldReasonsLength = this.detectionResult.reasons.length

      this.analyzeMovements()

      // Only notify if the detection result has changed in its 'isBot' status or reasons count
      if (
        oldIsBot !== this.detectionResult.isBot ||
        oldReasonsLength !== this.detectionResult.reasons.length
      ) {
        this._notifyListeners()
      }
    }

    if (this.mouseMovements.length > this.options.maxMovementPoints) {
      // Limit the number of points stored to avoid memory issues
      this.mouseMovements = this.mouseMovements.slice(
        this.mouseMovements.length - this.options.maxMovementPoints
      )
    }
  }

  /**
   * Analyse the collected mouse movement data.
   * Updates `this.detectionResult`.
   */
  private analyzeMovements(): void {
    // Reset the result to re-evaluate reasons with each analysis
    this.detectionResult.isBot = false
    this.detectionResult.reasons = []

    if (this.mouseMovements.length < this.options.minMovementPoints) {
      // Not enough data for meaningful analysis
      this.detectionResult.isBot = null // Still undetermined
      return
    }

    const firstPoint = this.mouseMovements[0]
    const lastPoint = this.mouseMovements[this.mouseMovements.length - 1]

    // Ensure there are at least two points for time calculation
    if (this.mouseMovements.length < 2 || lastPoint.time === firstPoint.time) {
      // Not enough movement or time passed to calculate speed.
      // This case might suggest immediate interaction without much movement.
      // We might consider this as a potential bot sign depending on context.
      // For now, just skip speed analysis and let other heuristics apply.
    } else {
      const totalDistance = this.calculateTotalDistance()
      const totalTime = lastPoint.time - firstPoint.time
      const avgSpeed = totalDistance / totalTime // Pixels per millisecond

      // 1. Check speed
      if (avgSpeed < this.options.minMovementSpeedPxPerMs) {
        this.detectionResult.isBot = true
        this.detectionResult.reasons.push(
          `Average speed too low (${avgSpeed.toFixed(2)} px/ms).`
        )
      }
      if (avgSpeed > this.options.maxMovementSpeedPxPerMs) {
        this.detectionResult.isBot = true
        this.detectionResult.reasons.push(
          `Average speed too high (${avgSpeed.toFixed(2)} px/ms).`
        )
      }
    }

    // 2. Check straightness (rough approximation)
    const straightness = this.checkStraightness()
    if (straightness < this.options.maxStraightnessDeviation) {
      // Smaller value = straighter
      this.detectionResult.isBot = true
      this.detectionResult.reasons.push(
        `Mouse path is unusually straight (${straightness.toFixed(3)}).`
      )
    }

    // 3. Check jitter (small, irregular movements)
    const jitterPercentage = this.checkJitter()
    if (jitterPercentage < this.options.minJitterPercentage) {
      this.detectionResult.isBot = true
      this.detectionResult.reasons.push(
        `Insufficient jitter in mouse movements (${(jitterPercentage * 100).toFixed(2)}%).`
      )
    }
  }

  /**
   * Calculates the total distance of mouse movements.
   * @returns {number} Total distance in pixels.
   */
  private calculateTotalDistance(): number {
    let distance = 0
    for (let i = 1; i < this.mouseMovements.length; i++) {
      const p1 = this.mouseMovements[i - 1]
      const p2 = this.mouseMovements[i]
      distance += Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2))
    }
    return distance
  }

  /**
   * Checks the straightness of the mouse path.
   * A lower number indicates a straighter line.
   * This is a simplified method: sum of distances of each point to the line
   * between the start and end points.
   * @returns {number} Measure of straightness.
   */
  private checkStraightness(): number {
    if (this.mouseMovements.length < 2) return 1 // Cannot be assessed

    const p1 = this.mouseMovements[0]
    const p2 = this.mouseMovements[this.mouseMovements.length - 1]

    // If start and end points are identical, a line cannot be formed.
    // This implies "jitter" in place, which is more human-like.
    if (p1.x === p2.x && p1.y === p2.y) {
      return 1 // Extremely crooked in terms of direct movement
    }

    let totalDeviation = 0
    const lineLength = Math.sqrt(
      Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2)
    )

    if (lineLength === 0) {
      // All points are at the same location
      return 1
    }

    for (let i = 1; i < this.mouseMovements.length - 1; i++) {
      const p = this.mouseMovements[i]
      // Distance of a point (x0, y0) to a line Ax + By + C = 0
      // A = y2 - y1
      // B = x1 - x2
      // C = -A*x1 - B*y1
      const A = p2.y - p1.y
      const B = p1.x - p2.x
      const C = -A * p1.x - B * p1.y

      const denominator = Math.sqrt(A * A + B * B)
      const distance =
        denominator === 0 ? 0 : Math.abs(A * p.x + B * p.y + C) / denominator
      totalDeviation += distance
    }
    // Normalize deviation relative to the length of the overall movement
    return totalDeviation / lineLength
  }

  /**
   * Checks the percentage of "jitter" movements.
   * Jitter refers to small, undirected movements typical of humans.
   * Here defined as movement where the distance to the next point is very small.
   * @returns {number} Percentage of jitter points.
   */
  private checkJitter(): number {
    if (this.mouseMovements.length < 3) return 1 // Not enough points for jitter analysis

    let jitterPoints = 0
    const smallMovementThreshold = 2 // Pixels, below which a movement is considered "small"

    for (let i = 1; i < this.mouseMovements.length; i++) {
      const p1 = this.mouseMovements[i - 1]
      const p2 = this.mouseMovements[i]
      const distance = Math.sqrt(
        Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2)
      )

      // If the movement is very small, we consider it potential jitter
      if (distance < smallMovementThreshold) {
        jitterPoints++
      }
    }
    return jitterPoints / this.mouseMovements.length
  }

  /**
   * Returns the current detection result.
   * @returns {DetectionResult}
   */
  public getResult(): DetectionResult {
    return this.detectionResult
  }

  /**
   * Resets the detector to start new measurements.
   */
  public reset(): void {
    this.stopMonitoring()
    this.mouseMovements = []
    this.detectionResult = {
      isBot: null,
      reasons: []
    }
    this._notifyListeners() // Notify listeners of reset state
  }
}

export default MouseBotDetector
